/**
 * Supabase CRUD for the `site_content_overrides` table.
 *
 * The table stores admin-edited i18n text overrides as flat
 * (lang, content_key, value) rows — see supabase-schema.sql section 13.
 * The ContentOverridesContext (src/contexts/ContentOverridesContext.tsx)
 * uses these functions to load all overrides on app boot and to persist
 * admin edits.
 *
 * RLS summary (enforced in Postgres, not just client-side):
 *   - SELECT: public — every visitor's boot-load succeeds without auth.
 *   - INSERT/UPDATE/DELETE: ADMIN only.
 */

import { supabase } from "./supabase";

export type ContentOverride = {
  id: string;
  lang: string;
  content_key: string;
  value: string;
  updated_at: string;
};

/** Load all overrides for all languages. Called once at app startup. */
export async function loadAllOverrides(): Promise<ContentOverride[]> {
  const { data, error } = await supabase
    .from("site_content_overrides")
    .select("id, lang, content_key, value, updated_at")
    .order("lang")
    .order("content_key");
  if (error) {
    // Non-fatal: if the table doesn't exist yet or is unreachable, the app
    // falls back to static CONTENT with no overrides applied.
    console.warn("[content-overrides] load:", error.message);
    return [];
  }
  return (data as ContentOverride[]) ?? [];
}

/**
 * Upsert a single override.
 * Uses the (lang, content_key) unique constraint — creates on first call,
 * replaces on subsequent calls for the same key.
 */
export async function upsertOverride(
  lang: string,
  content_key: string,
  value: string
): Promise<void> {
  const { error } = await supabase
    .from("site_content_overrides")
    .upsert(
      { lang, content_key, value, updated_at: new Date().toISOString() },
      { onConflict: "lang,content_key" }
    );
  if (error) throw new Error(`upsertOverride: ${error.message}`);
}

/**
 * Delete a single override, reverting the key to its static default.
 */
export async function deleteOverride(
  lang: string,
  content_key: string
): Promise<void> {
  const { error } = await supabase
    .from("site_content_overrides")
    .delete()
    .eq("lang", lang)
    .eq("content_key", content_key);
  if (error) throw new Error(`deleteOverride: ${error.message}`);
}
